// src/pages/TaskMarketplacePage.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Skeleton,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Pagination,
  Stack
} from '@mui/material';
import {
  Search,
  Sort,
  Refresh,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';
import TaskListItem from '../components/TaskListItem';
import DashboardLayout from '../components/DashboardLayout';

export default function TaskMarketplacePage() {
  const navigate = useNavigate();

  // State for user data
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  // State for TASK data and UI
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total_tasks: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  // State for Filters
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    sort: 'newest',
    userLat: null,
    userLng: null,
    maxDistance: 50
  });

  // === FETCH USER DATA ===
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetch('https://propertyearn.net/api/api/verify-token', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(response => {
        if (response.ok) return response.json();
        throw new Error('Invalid token');
      })
      .then(data => {
        setUser(data.user);
        setUserData({});
      })
      .catch(err => {
        console.error('Auth error:', err);
        localStorage.removeItem('token');
        navigate('/login');
      })
      .finally(() => setUserLoading(false));
  }, [navigate]);

  // === FETCH TASKS FUNCTION ===
  const fetchTasks = useCallback(async (page = 1) => {
    setTasksLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build query string from filters
      const params = new URLSearchParams({
        category: filters.category,
        sortBy: filters.sort,
        maxDistance: filters.maxDistance,
        page: page,
        per_page: pagination.per_page
      });
      
      // Add location to query if available
      if (filters.userLat && filters.userLng) {
        params.append('userLat', filters.userLat);
        params.append('userLng', filters.userLng);
      }

      console.log(`📡 Fetching page ${page} with ${pagination.per_page} tasks`);

      const response = await fetch(`https://propertyearn.net/api/api/marketplace-tasks?${params}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json(); 
      
      console.log('✅ API Response:', {
        page: page,
        tasksCount: data.tasks ? data.tasks.length : 0,
        pagination: data.pagination
      });

      // Handle different response structures
      let tasksArray = [];
      if (data.tasks) {
        tasksArray = data.tasks;
      } else if (Array.isArray(data)) {
        tasksArray = data;
      } else if (data.data && Array.isArray(data.data)) {
        tasksArray = data.data;
      }
      
      setTasks(tasksArray);
      
      // Update pagination if available from API
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          current_page: data.pagination.current_page || page,
          total_tasks: data.pagination.total_tasks || 0,
          total_pages: data.pagination.total_pages || 1,
          has_next: data.pagination.has_next || false,
          has_prev: data.pagination.has_prev || false
        }));
      }
      
    } catch (err) {
      console.error("❌ Failed to fetch tasks:", err);
      setError(err.message);
    } finally {
      setTasksLoading(false);
    }
  }, [filters.category, filters.sort, filters.maxDistance, filters.userLat, filters.userLng, pagination.per_page]);

  // === FETCH TASKS WHEN FILTERS CHANGE ===
  useEffect(() => {
    if (userLoading) return;
    // Reset to page 1 when filters change
    fetchTasks(1);
  }, [userLoading, fetchTasks]);

  // === GET USER'S LOCATION ===
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFilters(prev => ({
            ...prev,
            userLat: position.coords.latitude,
            userLng: position.coords.longitude
          }));
        },
        (err) => {
          console.log("Geolocation permission denied or failed.", err);
        }
      );
    }
  }, []);

  // === PAGINATION HANDLERS ===
  const handlePageChange = (event, newPage) => {
    console.log(`📄 Changing to page ${newPage}`);
    fetchTasks(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      handlePageChange(null, pagination.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) {
      handlePageChange(null, pagination.current_page - 1);
    }
  };

  // === FILTER HANDLERS ===
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleRefresh = () => {
    fetchTasks(pagination.current_page);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // === CLIENT-SIDE SEARCH FILTER ===
  const filteredTasks = tasks.filter(task => 
    task.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
    task.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
    task.address?.toLowerCase().includes(filters.search.toLowerCase())
  );

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardLayout user={user} userData={userData}>
      {/* Error Alert Snackbar */}
      <Snackbar open={!!error} autoHideDuration={10000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          Failed to load tasks: {error}
          <Button color="inherit" size="small" onClick={handleRefresh} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Snackbar>

      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Task Marketplace
        </Typography>
        <Button 
          startIcon={<Refresh />} 
          onClick={handleRefresh}
          disabled={tasksLoading}
          variant="outlined"
        >
          {tasksLoading ? <CircularProgress size={20} /> : 'Refresh'}
        </Button>
      </Box>

      {/* Search and Filter Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search tasks, locations..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Category"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="Photography">Photography</MenuItem>
              <MenuItem value="Verification">Verification</MenuItem>
              <MenuItem value="Research">Research</MenuItem>
              <MenuItem value="Survey">Survey</MenuItem>
              <MenuItem value="Virtual Tour">Virtual Tour</MenuItem>
              <MenuItem value="Data Entry">Data Entry</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Sort By"
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Sort />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="highest">Highest Pay</MenuItem>
              <MenuItem value="closest">Closest</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Chip 
              label={`${pagination.total_tasks} tasks`} 
              color={pagination.total_tasks > 0 ? "primary" : "default"}
              variant="outlined" 
            />         
          </Grid>
        </Grid>
      </Paper>

      {/* Pagination Controls - TOP */}
      {pagination.total_pages > 1 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Showing page {pagination.current_page} of {pagination.total_pages}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pagination.total_tasks} total tasks • {tasks.length} on this page
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                startIcon={<NavigateBefore />}
                onClick={handlePrevPage}
                disabled={!pagination.has_prev || tasksLoading}
                size="small"
              >
                Previous
              </Button>
              
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                disabled={tasksLoading}
                size="small"
                siblingCount={1}
                boundaryCount={1}
              />
              
              <Button
                endIcon={<NavigateNext />}
                onClick={handleNextPage}
                disabled={!pagination.has_next || tasksLoading}
                size="small"
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Main Content Area - Task List Only */}
      <Grid container spacing={3}>
        {/* Full Width Task List */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Available Tasks
            </Typography>
            {pagination.total_pages > 1 && (
              <Typography variant="body2" color="text.secondary">
                Page {pagination.current_page} • {tasks.length} tasks
              </Typography>
            )}
          </Box>
          
          <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {tasksLoading ? (
              // Show skeleton loaders while data is fetching
              [...Array(pagination.per_page)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={120} sx={{ mb: 1, borderRadius: 1 }} />
              ))
            ) : filteredTasks.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {filters.search ? 'No tasks match your search' : 'No tasks found'}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {filters.search 
                    ? 'Try adjusting your search terms or browse all tasks.' 
                    : 'There are no available tasks matching your current filters.'}
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => {
                    handleFilterChange('search', '');
                    handleFilterChange('category', 'all');
                  }}
                >
                  Clear Filters
                </Button>
              </Paper>
            ) : (
              filteredTasks.map((task) => (
                <TaskListItem
                  key={task.id}
                  task={task}
                  // Remove isSelected prop since we're removing the viewing function
                />
              ))
            )}
          </Box>

          {/* Pagination Controls - BOTTOM */}
          {pagination.total_pages > 1 && !tasksLoading && (
            <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
              <Stack direction="row" justifyContent="center" alignItems="center">
                <Button
                  startIcon={<NavigateBefore />}
                  onClick={handlePrevPage}
                  disabled={!pagination.has_prev}
                  sx={{ mr: 2 }}
                >
                  Previous
                </Button>
                
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={handlePageChange}
                  siblingCount={1}
                  boundaryCount={1}
                />
                
                <Button
                  endIcon={<NavigateNext />}
                  onClick={handleNextPage}
                  disabled={!pagination.has_next}
                  sx={{ ml: 2 }}
                >
                  Next
                </Button>
              </Stack>
            </Paper>
          )}
        </Grid>
      </Grid>
    </DashboardLayout>
  );
}